const DATABASE = "eternua-babysitting-v9";
const STORE = "offline-operations";
const VAULTS = "account-vaults";
const ALLOWED = new Set(["support_draft", "availability_draft", "incident_draft", "tour_progress"]);

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("Stockage hors connexion indisponible."));
    const request = indexedDB.open(DATABASE, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      // Legacy plaintext had no owner: it cannot safely be reassigned to an account.
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
      db.createObjectStore(STORE, { keyPath: "client_operation_id" });
      if (!db.objectStoreNames.contains(VAULTS)) db.createObjectStore(VAULTS, { keyPath: "account_id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Fermez les autres onglets pour sécuriser le coffre local."));
  });
}

async function requestStore(storeName, mode, work) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = work(tx.objectStore(storeName));
    tx.oncomplete = () => { const result = request.result; db.close(); resolve(result); };
    tx.onerror = tx.onabort = () => { db.close(); reject(tx.error || new Error("Échec du coffre local.")); };
  });
}

const database = {
  vault: (id) => requestStore(VAULTS, "readonly", (s) => s.get(id)),
  saveVault: (row) => requestStore(VAULTS, "readwrite", (s) => s.add(row)),
  rows: () => requestStore(STORE, "readonly", (s) => s.getAll()),
  put: (row) => requestStore(STORE, "readwrite", (s) => s.put(row)),
  remove: (id) => requestStore(STORE, "readwrite", (s) => s.delete(id)),
};

const bytes = (value) => new TextEncoder().encode(value);
const base64 = (value) => btoa(String.fromCharCode(...new Uint8Array(value)));
const unbase64 = (value) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
const context = (row) => JSON.stringify([row.account_id, row.client_operation_id, row.draft_key,
  row.operation, row.base_revision, row.expires_at]);

export function createOfflineVault(storage, cryptoApi, now = () => Date.now()) {
  let active = null;
  let generation = 0;
  const lock = () => { active = null; generation += 1; };
  const requireAccount = (accountId) => {
    if (!accountId || !active || active.accountId !== String(accountId) || now() > active.until) {
      lock();
      throw new Error("Déverrouillez le coffre de ce compte avant de continuer.");
    }
    return active;
  };
  async function seal(key, value, aad) {
    const iv = cryptoApi.getRandomValues(new Uint8Array(12));
    const encrypted = await cryptoApi.subtle.encrypt({ name: "AES-GCM", iv, additionalData: bytes(aad) }, key, bytes(JSON.stringify(value)));
    return { iv: base64(iv), ciphertext: base64(encrypted) };
  }
  async function unseal(key, row, aad) {
    const plaintext = await cryptoApi.subtle.decrypt({ name: "AES-GCM", iv: unbase64(row.iv), additionalData: bytes(aad) }, key, unbase64(row.ciphertext));
    return JSON.parse(new TextDecoder().decode(plaintext));
  }
  async function unlock(accountId, phrase) {
    lock();
    if (!accountId || typeof phrase !== "string" || phrase.length < 12) throw new Error("Une phrase secrète d’au moins 12 caractères est requise.");
    if (!cryptoApi?.subtle) throw new Error("Chiffrement indisponible : utilisez HTTPS ou localhost.");
    const epoch = generation;
    const id = String(accountId);
    let vault = await storage.vault(id);
    const salt = vault ? unbase64(vault.salt) : cryptoApi.getRandomValues(new Uint8Array(16));
    const material = await cryptoApi.subtle.importKey("raw", bytes(phrase), "PBKDF2", false, ["deriveKey"]);
    const key = await cryptoApi.subtle.deriveKey({ name: "PBKDF2", salt, iterations: 310000, hash: "SHA-256" }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    if (vault) {
      try { await unseal(key, vault, `vault:${id}`); } catch { throw new Error("Phrase secrète incorrecte ou coffre endommagé."); }
    } else {
      vault = { account_id: id, salt: base64(salt), ...await seal(key, { account: id }, `vault:${id}`) };
      await storage.saveVault(vault);
    }
    if (epoch !== generation) throw new Error("Le compte a changé pendant le déverrouillage.");
    active = { accountId: id, key, until: now() + 5 * 60 * 1000 };
    return true;
  }
  async function list(accountId) {
    if (!accountId) return [];
    const all = await storage.rows();
    for (const row of all) if (!row.expires_at || row.expires_at <= now()) await storage.remove(row.client_operation_id);
    return all.filter((row) => row.account_id === String(accountId) && row.expires_at > now())
      .map(({ ciphertext, iv, ...row }) => row);
  }
  async function queue(accountId, operation, payload, meta = {}) {
    const session = requireAccount(accountId);
    const epoch = generation;
    if (!ALLOWED.has(operation)) throw new Error("Opération hors connexion interdite.");
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) throw new Error("Brouillon invalide.");
    if (bytes(JSON.stringify(payload)).length > 16000) throw new Error("Brouillon trop volumineux.");
    const row = { account_id: session.accountId, client_operation_id: cryptoApi.randomUUID(),
      draft_key: meta.draft_key || cryptoApi.randomUUID(), base_revision: meta.base_revision || 0,
      operation, expires_at: now() + 24 * 60 * 60 * 1000 };
    const encrypted = { ...row, ...await seal(session.key, payload, context(row)) };
    if (epoch !== generation) throw new Error("Le compte a changé.");
    await storage.put(encrypted);
    return row;
  }
  async function read(accountId, operationId) {
    const session = requireAccount(accountId);
    const epoch = generation;
    const row = (await storage.rows()).find((item) => item.client_operation_id === operationId
      && item.account_id === session.accountId && item.expires_at > now());
    if (!row) throw new Error("Brouillon absent ou expiré.");
    const payload = await unseal(session.key, row, context(row));
    if (epoch !== generation) throw new Error("Le compte a changé.");
    return payload;
  }
  async function flush(accountId, send) {
    const session = requireAccount(accountId);
    const epoch = generation;
    await list(accountId);
    const rows = (await storage.rows()).filter((row) => row.account_id === session.accountId);
    let synced = 0;
    const failures = [];
    for (const row of rows) {
      requireAccount(accountId);
      if (epoch !== generation) throw new Error("Le compte a changé ; brouillon conservé.");
      if (row.expires_at <= now()) { await storage.remove(row.client_operation_id); continue; }
      try {
        const payload = await unseal(session.key, row, context(row));
        if (epoch !== generation) throw new Error("Le compte a changé ; brouillon conservé.");
        const ack = await send({ account_id: session.accountId, client_operation_id: row.client_operation_id,
          draft_key: row.draft_key, base_revision: row.base_revision, operation: row.operation, payload });
        if (epoch !== generation || ack?.durable !== true || ack?.status !== "stored"
            || String(ack.account_id) !== session.accountId || ack.client_operation_id !== row.client_operation_id
            || ack.draft_key !== row.draft_key || !Number.isInteger(ack.revision) || ack.revision < 1 || !ack.draft_id) {
          throw new Error("Confirmation durable manquante ; le brouillon reste sur cet appareil.");
        }
        await storage.remove(row.client_operation_id);
        synced += 1;
      } catch (error) {
        if (epoch !== generation) throw error;
        // One conflicted draft must not block unrelated drafts or a new copy.
        failures.push(error);
      }
    }
    if (failures.length) throw new Error(`${synced} synchronisé(s), ${failures.length} conservé(s) : ${failures[0].message}`);
    return synced;
  }
  return { unlock, lock, queue, list, flush, read };
}

const vault = createOfflineVault(database, typeof window !== "undefined" ? window.crypto : undefined);
export const unlockBabysittingOfflineVault = vault.unlock;
export const lockBabysittingOfflineVault = vault.lock;
export const queueBabysittingOfflineOperation = vault.queue;
export const listBabysittingOfflineOperations = vault.list;
export const flushBabysittingOfflineOperations = vault.flush;
export const readBabysittingOfflineOperation = vault.read;

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", vault.lock);
  window.addEventListener("storage", vault.lock);
  document.addEventListener("visibilitychange", () => { if (document.hidden) vault.lock(); });
}
